import {ArrayElementOf} from '../services/util'

export type EntityReference = {
  id: number
  name: string
}

export type JournalRecord = {
  id: number
  plant?: EntityReference
  project?: EntityReference
  photos?: string[]
  comment?: string
  creationDateUtc?: Date
  startDateUtc?: Date
  endDateUtc?: Date
  actualCompletionDateUtc: Date
  type?: JournalEventType
}

export const PredefinedToDoTypes = [
  'preplanting',
  'seedsowing',
  'prickingout',
  'hardeningoff',
  'transplanting',
  'planting',
  'repotting',
  'propagation',
  'watering',
  'weeding',
  'fertilizing',
  'chemicals',
  'harvesting',
  'pruning',
  'winter',
  'buying',
  'general',
] as const

export const PredefinedEventTypes = [
  'preplanting',
  'seedsowing',
  'seedlingsemerge',
  'trueleaves',
  'prickingout',
  'hardeningoff',
  'transplanting',
  'planting',
  'repotting',
  'propagation',
  'watering',
  'weeding',
  'fertilizing',
  'chemicals',
  'blooming',
  'harvesting',
  'pruning',
  'winter',
  'general',
] as const

export type PredefinedToDoTypeId = (typeof PredefinedToDoTypes)[number]
export type PredefinedEventTypeId = (typeof PredefinedEventTypes)[number]

export type ToDoTypeId = PredefinedToDoTypeId | 'custom'
export type EventTypeId = PredefinedEventTypeId | 'custom'

export type ToDoEventTypeInfo = {
  name: string
  color: ToDoTypeColor
  icon: string
}

const toDoTypeColors = ['green', 'purple', 'brown', 'yellow', 'red', 'blue', 'gray'] as const

type ToDoTypeColor = (typeof toDoTypeColors)[number]

// When changing task or event types, you must manually adjust the enum values ​​in the database

// Now the type id and its name must match, because there are components (for example, ToDoTypeFormSelector) that only pass the name without the id

export const ToDoTypeMetadata: Record<PredefinedToDoTypeId, ToDoEventTypeInfo> = {
  preplanting: {
    name: 'preplanting',
    color: 'brown',
    icon: '🫚',
  },
  seedsowing: {
    name: 'seedsowing',
    color: 'brown',
    icon: '🫘',
  },
  prickingout: {
    name: 'prickingout',
    color: 'green',
    icon: '🪴',
  },
  hardeningoff: {
    name: 'hardeningoff',
    color: 'blue',
    icon: '🌬️',
  },
  transplanting: {
    name: 'transplanting',
    color: 'brown',
    icon: '🌼',
  },
  planting: {name: 'planting', color: 'green', icon: '🌱'},
  watering: {
    name: 'watering',
    color: 'blue',
    icon: '💧',
  },
  repotting: {
    name: 'Repotting',
    icon: '🪴',
    color: 'green',
  },
  weeding: {name: 'weeding', color: 'brown', icon: '🧤'},
  fertilizing: {name: 'fertilizing', color: 'yellow', icon: '💊'},
  chemicals: {
    name: 'chemicals',
    color: 'red',
    icon: '💉',
  },
  propagation: {
    name: 'Propagation',
    icon: '🌿',
    color: 'green',
  },
  harvesting: {name: 'harvesting', color: 'purple', icon: '🍎'},
  pruning: {name: 'pruning', color: 'green', icon: '🌳'},
  winter: {
    name: 'winter',
    color: 'blue',
    icon: '❄️',
  },
  buying: {
    name: 'buying',
    color: 'purple',
    icon: 'shopping_bags',
  },
  general: {
    name: 'general',
    color: 'green',
    icon: '☑️',
  },
}

// When changing task or event types, you must manually adjust the enum values ​​in the database

// Now the type id and its name must match, because there are components (for example, ToDoTypeFormSelector) that only pass the name without the id

export const EventTypeMetadata: Record<PredefinedEventTypeId, ToDoEventTypeInfo> = {
  preplanting: {
    name: 'preplanting',
    color: 'brown',
    icon: '🫚',
  },
  seedsowing: {
    name: 'seedsowing',
    color: 'brown',
    icon: '🫘',
  },
  seedlingsemerge: {
    name: 'seedlingsemerge',
    color: 'green',
    icon: '🌱',
  },
  trueleaves: {
    name: 'trueleaves',
    color: 'green',
    icon: '🍃',
  },
  prickingout: {
    name: 'prickingout',
    color: 'green',
    icon: '🪴',
  },
  hardeningoff: {
    name: 'hardeningoff',
    color: 'blue',
    icon: '🌬️',
  },
  transplanting: {
    name: 'transplanting',
    color: 'brown',
    icon: '🌼',
  },
  planting: {name: 'planting', color: 'green', icon: '🌱'},
  watering: {
    name: 'watering',
    color: 'blue',
    icon: '💧',
  },
  repotting: {
    name: 'Repotting',
    icon: '🪴',
    color: 'green',
  },
  propagation: {
    name: 'Propagation',
    icon: '🌿',
    color: 'green',
  },
  weeding: {name: 'weeding', color: 'brown', icon: '🧤'},
  fertilizing: {name: 'fertilizing', color: 'yellow', icon: '💊'},
  chemicals: {
    name: 'chemicals',
    color: 'red',
    icon: '💉',
  },
  blooming: {
    name: 'blooming',
    color: 'red',
    icon: '🌺',
  },
  harvesting: {name: 'harvesting', color: 'purple', icon: '🍎'},
  pruning: {name: 'pruning', color: 'green', icon: '🌳'},
  winter: {
    name: 'winter',
    color: 'blue',
    icon: '❄️',
  },
  general: {
    name: 'general',
    color: 'green',
    icon: '☑️',
  },
}

export const ToDoEventTypesMatching: Record<PredefinedToDoTypeId, PredefinedEventTypeId> = {
  seedsowing: 'seedsowing',
  prickingout: 'prickingout',
  hardeningoff: 'hardeningoff',
  transplanting: 'transplanting',
  planting: 'planting',
  repotting: 'repotting',
  propagation: 'propagation',
  harvesting: 'harvesting',
  pruning: 'pruning',
  weeding: 'weeding',
  fertilizing: 'fertilizing',
  watering: 'watering',
  chemicals: 'chemicals',
  buying: 'general',
  general: 'general',
  preplanting: 'preplanting',
  winter: 'winter',
}

export type PredefinedToDoType = {
  type: PredefinedToDoTypeId
}

export type CustomToDoType = {
  type: 'custom'
  // deleting a custom type leaves the reference behind on the tasks that used it
  customToDoType: CustomType | null
}

export type ToDoType = PredefinedToDoType | CustomToDoType

export type CustomType = {
  id: number
  name: string
  icon: string
}

// A type as the forms offer it: a predefined type has no id of its own, a
// user-defined one is identified by the id of the custom type behind it.
export type ToDoTypeOption = {
  id: number | null
  name: string
  icon: string
}

export type PredefinedJournalEventType = {
  type: PredefinedEventTypeId
}

export type CustomJournalEventType = {
  type: 'custom'
  customJournalEventType: CustomType
}

export type JournalEventType = PredefinedJournalEventType | CustomJournalEventType

const UnknownToDoType: ToDoEventTypeInfo = {icon: '📄', name: '', color: 'gray'}
export const DefaultEventType: ToDoEventTypeInfo = {icon: '📄', name: 'general', color: 'gray'}

// the type is optional on both records, and the callers pass it straight through
export function getToDoTypeInfo(type?: ToDoType): ToDoEventTypeInfo {
  if (!type || !type.type) {
    return UnknownToDoType
  }

  if (type.type === 'custom') {
    if (!type.customToDoType) {
      return UnknownToDoType
    } else {
      return {
        icon: type.customToDoType.icon,
        name: type.customToDoType.name,
        color: toDoTypeColors[type.customToDoType.id % toDoTypeColors.length],
      }
    }
  } else {
    return ToDoTypeMetadata[type.type]
  }
}

export function getJournalEventTypeInfo(type?: JournalEventType): ToDoEventTypeInfo {
  if (!type || !type.type) {
    return DefaultEventType
  }

  if (type.type === 'custom') {
    if (!type.customJournalEventType) {
      return DefaultEventType
    } else {
      return {
        icon: type.customJournalEventType.icon,
        name: type.customJournalEventType.name,
        color: toDoTypeColors[type.customJournalEventType.id % toDoTypeColors.length],
      }
    }
  } else {
    return EventTypeMetadata[type.type]
  }
}
